/**
 * merge-alarm-sources — combines the two alarm data sources into one list.
 *
 * WHY TWO SOURCES (djin-cloud#628)
 *
 * Active and historical alarms live in different tables and cannot be served
 * by one call. Cleared rows are purged from `alarms.active_alarms` roughly a
 * minute after clearing (#434); the durable record is `alarms.alarm_history`.
 * Measured on POD: active_alarms held 0 cleared rows against 21,376 cleared
 * events in alarm_history.
 *
 * The status filter is multi-select, so "Needs Attention + History" is a
 * legitimate selection spanning both. The previous routing collapsed a
 * set-valued filter into a single scalar —
 *
 *     status: filter.status.length === 1 ? filter.status[0] : undefined
 *
 * — and then branched on `status === 'history'`. With two buckets selected the
 * scalar was `undefined`, so the branch fell through to the active source and
 * every historical row silently vanished. "Both" degraded to "neither", and
 * the degradation was invisible because the active rows still rendered.
 *
 * Hence this module: when a selection spans both sources, fetch both and merge
 * here rather than picking one.
 */

import type { ActiveAlarm } from '@/types/alarm.types';

/**
 * Identity of a single raise episode.
 *
 * Alarm identity proper is (site_id, device_id, condition_key), but that is
 * NOT enough to dedupe against history: a condition that raised, cleared, and
 * re-raised is two genuinely distinct episodes sharing one identity, and
 * collapsing them would erase the earlier one from History. `raised_at`
 * separates the episodes.
 *
 * KEY_SEP is NUL because it cannot occur in a site id, device id, condition
 * key, or ISO timestamp, so no value can forge a key boundary. It is written
 * as an ESCAPE, never as a literal control character: a raw NUL in the source
 * makes git classify the whole file as binary, silently dropping it out of
 * every future line-level diff and code review.
 */
const KEY_SEP = '\u0000';

function episodeKey(a: ActiveAlarm): string {
  return [a.site_id, a.device_id, a.condition_key, a.raised_at].join(KEY_SEP);
}

/**
 * Merge active-source and history-source rows, preferring the active row on
 * collision.
 *
 * Collisions are real, not theoretical: during the ~60s purge window a
 * just-cleared alarm exists in BOTH tables for the same episode. The active
 * row wins because it carries authoritative live state (ack, stale, latching),
 * while the history row is a projection reconstructed from a terminal event.
 * Preferring history would let a currently-raised alarm render as historical —
 * the worst possible direction for this bug.
 *
 * Order: active rows first, then history rows. Both pages group and sort
 * downstream, so this only fixes a deterministic starting order.
 */
export function mergeAlarmSources(
  active: ActiveAlarm[],
  history: ActiveAlarm[],
): ActiveAlarm[] {
  const seen = new Set(active.map(episodeKey));
  const merged = [...active];

  for (const row of history) {
    const key = episodeKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }

  return merged;
}

/**
 * Which sources a status selection requires.
 *
 * An empty selection means "no bucket filter", which is the active list — the
 * pages' default view. It must NOT be read as "everything including history",
 * or clearing the filters would silently pull 30 days of cleared alarms into
 * the operator's default view.
 */
export function sourcesForStatuses(statuses: readonly string[]): {
  active: boolean;
  history: boolean;
} {
  const history = statuses.includes('history');
  const active = statuses.length === 0 || statuses.some((s) => s !== 'history');
  return { active, history };
}

/**
 * The single-bucket server-side pre-filter, or `undefined` when the selection
 * spans several buckets and the server cannot express it.
 *
 * Returning `undefined` here is safe in a way it previously was not: the
 * caller now decides which SOURCES to hit from `sourcesForStatuses`, so an
 * unexpressible server filter only widens the payload (the client narrows it),
 * instead of silently selecting the wrong table.
 */
export function serverStatusFilter(
  statuses: readonly string[],
): string | undefined {
  return statuses.length === 1 ? statuses[0] : undefined;
}
